import { useState } from "react";
import { v4 as uuidv4 } from "uuid";
import {
  QBQuestionForm,
  QBTheme,
  QBFormConfig,
  QBQuestion,
  QBTopic,
  QBLearningObjective,
  Theme,
  Topic,
  LearningObjective,
  InputKnowledgeBase,
  TransformedQBData,
  TransformedQBTopic,
  TransformedQBLO,
  QuestionFileMap,
  QBConfigInput,
} from "src/interfaces/KnowledgeBase";
import { stringValidator, formatString } from "src/utils/helpers";
import { cloneDeep, isEqual } from "lodash";

export const useQuestionForm = (): QBQuestionForm => {
  const [qbThemes, setQBThemes] = useState<QBTheme[]>([
    {
      name: "",
      label: "Theme",
      type: "select",
      options: [],
      selected: null,
      topics: [
        {
          name: "",
          options: [],
          selected: null,
          learning_objectives_options: [],
          questions: [
            {
              uuid: uuidv4(),
              item_demand: {
                label: "Item Demand",
                value: "",
                type: "select",
                selected: null,
                options: [],
              },
              answer: "",
              explanation: "",
              image: { name: "", value: "", file: null },
              objectives: [],
            },
          ],
        },
      ],
    },
  ]);
  const [qbConfig, setQBConfig] = useState<QBFormConfig | null>(null);
  const [transformedQBData, setTransformedQBData] = useState<
    TransformedQBData[] | null
  >(null);
  const [initialQbThemes, setInitQBThemes] = useState<QBTheme[] | null>(null);

  const setQBConfigFromStructure = (structure: string) => {
    try {
      const parsed = JSON.parse(structure);

      const themes = parsed?.themes ?? {};
      const firstThemeKey = Object.keys(themes)[0];
      const firstTheme = themes[firstThemeKey] ?? {};

      const topics = firstTheme.topics ?? {};
      const firstTopicKey = Object.keys(topics)[0];
      const firstTopic = topics[firstTopicKey] ?? {};

      const questions = firstTopic.questions ?? {};
      const firstQuestionKey = Object.keys(questions)[0];
      const firstQ = questions[firstQuestionKey] ?? {};

      const configObj = {
        themeLabel: firstTheme.label || "Theme",
        topicLabel: firstTopic.label || "Topic",
        learningObjectiveLabel: "Learning Objective",
        questionLabel: "Question",
        itemDemandLabel: firstQ.item_demand?.label || "Item Demand",
        answerLabel: firstQ.answer?.label || "Answer",
        explanationLabel: firstQ.explanation?.label || "Explanation",
        hasExplanation: !!firstQ.explanation,
        imageLabel: firstQ.image?.label || "Item",
        imageSummaryLabel: firstQ.image?.question_summary || "Image Summary",
        itemDemandOptions: firstQ.item_demand?.options || [],
      };

      setQBConfig(configObj);

      setQBThemes([
        {
          name: "",
          label: configObj.themeLabel,
          type: "select",
          options:
            transformedQBData?.map((theme: TransformedQBData) => theme.name) ??
            [],
          selected: null,
          topics: [
            {
              name: "",
              options:
                transformedQBData?.flatMap((theme: TransformedQBData) =>
                  theme.topics.map((topic: TransformedQBTopic) => topic.name)
                ) ?? [],
              selected: null,
              learning_objectives_options:
                transformedQBData?.flatMap((theme: TransformedQBData) =>
                  theme.topics.flatMap((topic: TransformedQBTopic) =>
                    topic.learning_objectives.map((lo) => lo.name)
                  )
                ) ?? [],
              questions: [
                {
                  uuid: uuidv4(),
                  item_demand: {
                    label: configObj.itemDemandLabel,
                    value: "",
                    type: "select",
                    selected: null,
                    options: configObj.itemDemandOptions,
                  },
                  answer: "",
                  explanation: "",
                  image: {
                    name: "",
                    value: "",
                    file: null,
                  },
                  objectives: [],
                },
              ],
            },
          ],
        },
      ]);

      console.log("QB Themes:", qbThemes, "QB Config:", qbConfig);
    } catch (error) {
      console.error("Error parsing structure:", error);
      setQBConfig(null);
    }
  };

  const addQBTheme = () => {
    setQBThemes([
      ...qbThemes,
      {
        name: "",
        label: qbConfig?.themeLabel || "Theme",
        type: "select",
        options:
          transformedQBData?.map((theme: TransformedQBData) => theme.name) ||
          [],
        selected: null,
        topics: [
          {
            name: "",
            options:
              transformedQBData?.flatMap((theme: TransformedQBData) =>
                theme.topics.map((topic: TransformedQBTopic) => topic.name)
              ) || [],
            selected: null,
            learning_objectives_options:
              transformedQBData?.flatMap((theme: TransformedQBData) =>
                theme.topics.flatMap((topic: TransformedQBTopic) =>
                  topic.learning_objectives.map((lo) => lo.name)
                )
              ) || [],
            questions: [
              {
                uuid: uuidv4(),
                item_demand: {
                  label: qbConfig?.itemDemandLabel || "Item Demand",
                  value: "",
                  type: "select",
                  selected: null,
                  options: qbConfig?.itemDemandOptions || [],
                },
                answer: "",
                explanation: "",
                image: {
                  name: "",
                  value: "",
                  file: null,
                },
                objectives: [],
              },
            ],
          },
        ],
      },
    ]);
  };

  const removeQBTheme = (themeIndex: number) => {
    if (qbThemes.length <= 1) return;
    setQBThemes(qbThemes.filter((_, idx) => idx !== themeIndex));
  };

  const addQBTopic = (themeIndex: number) => {
    const newThemes = cloneDeep(qbThemes);

    newThemes[themeIndex].topics.push({
      name: "",
      options:
        transformedQBData?.flatMap((theme: TransformedQBData) =>
          theme.topics.map((topic: TransformedQBTopic) => topic.name)
        ) || [],
      selected: null,
      learning_objectives_options:
        transformedQBData?.flatMap((theme: TransformedQBData) =>
          theme.topics.flatMap((topic: TransformedQBTopic) =>
            topic.learning_objectives.map((lo) => lo.name)
          )
        ) || [],
      questions: [
        {
          uuid: uuidv4(),
          item_demand: {
            label: qbConfig?.itemDemandLabel || "Item Demand",
            value: "",
            type: "select",
            options: qbConfig?.itemDemandOptions || [],
            selected: null,
          },
          answer: "",
          explanation: "",
          image: { name: "", value: "", file: null },
          objectives: [],
        },
      ],
    });

    setQBThemes(newThemes);
  };

  const removeQBTopic = (themeIndex: number, topicIndex: number) => {
    const newThemes = cloneDeep(qbThemes);
    if (newThemes[themeIndex].topics.length <= 1) return;
    newThemes[themeIndex].topics.splice(topicIndex, 1);
    setQBThemes(newThemes);
  };

  const addQBQuestion = (themeIndex: number, topicIndex: number) => {
    const newThemes = cloneDeep(qbThemes);
    newThemes[themeIndex].topics[topicIndex].questions.push({
      uuid: uuidv4(),
      item_demand: {
        label: qbConfig?.itemDemandLabel || "Item Demand",
        value: "",
        type: "select",
        options: qbConfig?.itemDemandOptions || [],
        selected: null,
      },
      answer: "",
      explanation: "",
      image: { name: "", value: "", file: null },
      objectives: [],
    });
    setQBThemes(newThemes);
  };

  const removeQBQuestion = (
    themeIndex: number,
    topicIndex: number,
    questionIndex: number
  ) => {
    const newThemes = cloneDeep(qbThemes);
    if (newThemes[themeIndex].topics[topicIndex].questions.length <= 1) return;
    newThemes[themeIndex].topics[topicIndex].questions.splice(questionIndex, 1);
    setQBThemes(newThemes);
  };

  const updateQBQuestion = (
    themeIndex: number,
    topicIndex: number,
    questionIndex: number,
    question: QBQuestion
  ) => {
    const newThemes = cloneDeep(qbThemes);
    newThemes[themeIndex].topics[topicIndex].questions[questionIndex] = {
      ...question,
    };
    setQBThemes(newThemes);
  };

  const validateQBForm = () => {
    return (
      qbThemes.length > 0 &&
      qbThemes.every(
        (theme) =>
          stringValidator(theme.name) &&
          theme.topics.length > 0 &&
          theme.topics.every(
            (topic) =>
              stringValidator(topic.name) &&
              topic.questions.length > 0 &&
              topic.questions.every(
                (q) =>
                  stringValidator(q.uuid) &&
                  stringValidator(q.item_demand.value) &&
                  stringValidator(q.answer) &&
                  (qbConfig?.hasExplanation
                    ? stringValidator(q.explanation)
                    : true) &&
                  (stringValidator(q.image.name) ||
                    stringValidator(q.image.value)) &&
                  q.objectives.length > 0
              )
          )
      )
    );
  };

  const resetQBForm = () => {
    setQBThemes([
      {
        name: "",
        label: qbConfig?.themeLabel || "Theme",
        type: "select",
        options: [],
        selected: null,
        topics: [
          {
            name: "",
            options: [],
            selected: null,
            learning_objectives_options: [],
            questions: [
              {
                uuid: uuidv4(),
                item_demand: {
                  label: qbConfig?.itemDemandLabel || "Item Demand",
                  value: "",
                  type: "select",
                  options: [],
                  selected: null,
                },
                answer: "",
                explanation: "",
                image: {
                  name: "",
                  value: "",
                  file: null,
                },
                objectives: [],
              },
            ],
          },
        ],
      },
    ]);
  };

  const getQBFormData = () => {
    if (!qbConfig) return null;
    const result: any = { themes: [] };
    qbThemes.forEach((theme) => {
      const themeData: any = {
        name: theme.name,
        label: qbConfig.themeLabel,
        type: "select",
        topics: [],
      };
      theme.topics.forEach((topic) => {
        const topicData: any = {
          name: topic.name,
          label: qbConfig.topicLabel,
          type: "select",
          questions: [],
        };
        topic.questions.forEach((q) => {
          const questionObj: any = {
            uuid: q.uuid,
            item_demand: {
              label: qbConfig.itemDemandLabel,
              value: q.item_demand,
              type: "select",
              options: qbConfig.itemDemandOptions,
            },
            answer: {
              label: qbConfig.answerLabel,
              value: q.answer,
              type: "text",
            },
            image: {
              label: qbConfig.imageLabel,
              name: q.image.name,
              value: q.image.value,
              type: "image",
            },
            objectives: q.objectives,
          };

          if (qbConfig.hasExplanation) {
            questionObj.explanation = {
              label: qbConfig.explanationLabel,
              value: q.explanation,
              type: "textarea",
            };
          }

          topicData.questions.push(questionObj);
        });
        themeData.topics.push(topicData);
      });
      result.themes.push(themeData);
    });
    return result;
  };

  const prefillQBForm = (data: any, prevQBData?: TransformedQBData[]) => {
    try {
      if (!data.themes || typeof data.themes !== "object") {
        throw new Error("Invalid data: missing themes");
      }

      const themes: QBTheme[] = [];

      for (const [themeName, themeData] of Object.entries<any>(data.themes)) {
        // get this theme from transformedThemes
        const transformedTheme = prevQBData?.find((t) => t.name === themeName);

        const topics: QBTopic[] = [];

        for (const [topicName, topicData] of Object.entries<any>(
          themeData.topics || {}
        )) {
          // get this topic from transformedTheme
          const transformedTopic = transformedTheme?.topics.find(
            (t) => t.name === topicName
          );

          const questions: QBQuestion[] = [];

          for (const [qId, qData] of Object.entries<any>(
            topicData.questions || {}
          )) {
            // Handle backend typos: _demand vs item_demand
            const itemDemandData = qData.item_demand || qData._demand || {};
            // Handle backend typos: objective vs objectives
            const objectivesData = qData.objectives || (qData.objective ? [qData.objective] : []);

            questions.push({
              uuid: qId,
              item_demand: {
                label: itemDemandData?.label || "Item Demand",
                value:
                  itemDemandData?.value?.charAt(0).toUpperCase() +
                    itemDemandData?.value?.slice(1) || "",
                type: "select",
                options: itemDemandData?.options || [],
                selected: itemDemandData?.value || null,
              },
              answer: qData.answer?.value || "",
              explanation: qData.explanation?.value || "",
              image: {
                name: qData.image?.name || "",
                value: qData.image?.value || "",
                file: null, // file can't be reconstructed, only url
              },
              objectives: objectivesData,
            });
          }

          topics.push({
            name: topicName,
            options: transformedTheme?.topics.map((t) => t.name) || [],
            selected: topicName,
            learning_objectives_options:
              transformedTopic?.learning_objectives.map((lo) => lo.name) || [],
            questions,
          });
        }

        themes.push({
          name: themeName,
          label: themeData.label || "Theme",
          type: themeData.type || "select",
          options: prevQBData?.map((t) => t.name) || [],
          selected: themeName,
          topics,
        });
      }

      // Config inference
      const firstTheme: any = Object.values(data.themes)[0];
      const firstTopic: any = firstTheme?.topics
        ? Object.values(firstTheme.topics)[0]
        : {};
      const firstQ: any = firstTopic?.questions
        ? Object.values(firstTopic.questions)[0]
        : {};

      const qbConfig: QBFormConfig = {
        themeLabel: firstTheme?.label || "Theme",
        topicLabel: firstTopic?.label || "Topic",
        learningObjectiveLabel: "Learning Objective",
        questionLabel: "Question",
        itemDemandLabel: firstQ?.item_demand?.label || "Item Demand",
        answerLabel: firstQ?.answer?.label || "Answer",
        explanationLabel: firstQ?.explanation?.label || "Explanation",
        hasExplanation: !!firstQ?.explanation,
        imageLabel: firstQ?.image?.label || "Item",
        imageSummaryLabel: firstQ?.image?.image_summary || "Image Summary",
        itemDemandOptions: firstQ?.item_demand?.options || [],
      };

      console.log("=== PREFILL QB FORM DEBUG ===");
      console.log("First theme:", themes[0]?.name);
      console.log("First topic:", themes[0]?.topics[0]?.name);
      console.log("First topic learning_objectives_options:", themes[0]?.topics[0]?.learning_objectives_options);
      console.log("First question objectives:", themes[0]?.topics[0]?.questions[0]?.objectives);
      console.log("Do they match?", themes[0]?.topics[0]?.learning_objectives_options?.some(opt =>
        themes[0]?.topics[0]?.questions[0]?.objectives?.includes(opt)
      ));

      setQBThemes(themes);
      setQBConfig(qbConfig);
      setInitQBThemes(themes);
    } catch (error) {
      console.error("Error prefilling QB form:", error);
    }
  };

  const transformKnowledgeBase = (jsonString: string): TransformedQBData[] => {
    try {
      const parsed: InputKnowledgeBase = JSON.parse(jsonString);

      if (!parsed.themes || typeof parsed.themes !== "object") {
        throw new Error("Invalid themes structure");
      }

      const transformedThemes: TransformedQBData[] = [];

      for (const [themeName, themeData] of Object.entries(parsed.themes)) {
        if (typeof themeData !== "object" || themeData === null) continue;

        const transformedTopics: TransformedQBTopic[] = [];

        if (themeData.topics && typeof themeData.topics === "object") {
          for (const [topicName, topicData] of Object.entries(
            themeData.topics
          )) {
            if (typeof topicData !== "object" || topicData === null) continue;

            const transformedLOs: TransformedQBLO[] = [];

            // Handle new structure: extract LOs from questions
            if (topicData.questions && typeof topicData.questions === "object") {
              const uniqueLOs = new Set<string>();

              for (const [, questionData] of Object.entries<any>(topicData.questions)) {
                // Handle backend typos: objective vs objectives
                const objectivesArray = questionData.objectives ||
                  (questionData.objective ? [questionData.objective] : []);

                if (Array.isArray(objectivesArray)) {
                  objectivesArray.forEach((lo: string) => uniqueLOs.add(lo));
                }
              }

              console.log(`=== Transform KB: Topic ${topicName} ===`);
              console.log("Unique LOs extracted:", Array.from(uniqueLOs));

              uniqueLOs.forEach(loName => {
                transformedLOs.push({
                  name: loName,
                  questions: {
                    name: loName,
                    content: undefined,
                  },
                });
              });
            }
            // Handle old structure: LOs directly under topics
            else if (
              topicData.learning_objectives &&
              typeof topicData.learning_objectives === "object"
            ) {
              for (const [loName, loData] of Object.entries(
                topicData.learning_objectives
              )) {
                if (typeof loData !== "object" || loData === null) continue;

                transformedLOs.push({
                  name: loName,
                  questions: {
                    name: loName,
                    content:
                      loData.content &&
                      typeof loData.content === "object" &&
                      typeof loData.content.value === "string"
                        ? loData.content.value
                        : undefined,
                  },
                });
              }
            }

            transformedTopics.push({
              name: topicName,
              learning_objectives: transformedLOs,
            });
          }
        }

        transformedThemes.push({
          name: themeName,
          topics: transformedTopics,
        });
      }

      setTransformedQBData(transformedThemes);
      return transformedThemes;
    } catch (error) {
      console.error("Error transforming knowledge base:", error);
      return [];
    }
  };

  const createTransformedQB = (
    json: JSON
  ): { newThemes: QBTheme[]; newQbData: TransformedQBData[] } => {
    const themes = transformKnowledgeBase(JSON.stringify(json));

    const themesNames = themes.map((theme: Theme) => theme.name);
    const topicsNames = themes.flatMap((theme: Theme) =>
      theme.topics.map((topic: Topic) => topic.name)
    );
    const learningObjectivesNames = themes.flatMap((theme: Theme) =>
      theme.topics.flatMap((topic: Topic) =>
        (topic.learning_objectives || []).map((lo: LearningObjective) => lo.name)
      )
    );

    const updatedThemes = qbThemes.map((theme: QBTheme) => ({
      ...theme,
      options: themesNames,
      selected: "",
      topics: theme.topics.map((topic: QBTopic) => ({
        ...topic,
        options: topicsNames,
        selected: "",
        learning_objectives_options: learningObjectivesNames,
      })),
    }));
    setQBThemes(updatedThemes);
    return {
      newThemes: updatedThemes,
      newQbData: themes,
    };
  };

  const updateSelectedTheme = (themeName: string, themeIndex: number) => {
    setQBThemes((prevThemes) => {
      const newThemes = [...prevThemes];
      const selectedThemeData = transformedQBData?.find(
        (t) => t.name === themeName
      );

      if (!selectedThemeData) return prevThemes;

      const updatedTheme = { ...newThemes[themeIndex] };
      updatedTheme.name = themeName;
      updatedTheme.selected = themeName;
      updatedTheme.options = transformedQBData?.map((t) => t.name) || [];

      // update topics' options only
      updatedTheme.topics = updatedTheme.topics.map((topic) => {
        return {
          ...topic,
          options: selectedThemeData.topics.map((t) => t.name),
          name: "",
          selected: null,
          learning_objectives_options: selectedThemeData.topics.flatMap((t) =>
            t.learning_objectives.map((l) => l.name)
          ),
        };
      });

      newThemes[themeIndex] = updatedTheme;
      return newThemes;
    });
  };

  const updateSelectedTopic = (
    topicName: string,
    themeIndex: number,
    topicIndex: number
  ) => {
    setQBThemes((prevThemes) => {
      const newThemes = [...prevThemes];
      const updatedTheme = { ...newThemes[themeIndex] };
      const updatedTopics = [...updatedTheme.topics];
      const selectedThemeData = transformedQBData?.find(
        (t) => t.name === updatedTheme.name
      );
      if (!selectedThemeData) return prevThemes;
      const selectedTopicData = selectedThemeData.topics.find(
        (t) => t.name === topicName
      );
      if (!selectedTopicData) return prevThemes;

      const selectedLOs = selectedTopicData.learning_objectives.map(
        (lo) => lo.name
      );

      const updatedTopic = {
        ...updatedTopics[topicIndex],
        selected: topicName,
        name: topicName,
        learning_objectives_options: selectedLOs,
        questions: [
          {
            uuid: uuidv4(),
            item_demand: {
              label: qbConfig?.itemDemandLabel || "",
              value: "",
              type: "select",
              selected: null,
              options: qbConfig?.itemDemandOptions || [],
            },
            answer: "",
            explanation: "",
            image: {
              name: "",
              value: "",
              file: null,
            },
            objectives: [],
          },
        ],
      };

      updatedTopics[topicIndex] = updatedTopic;
      updatedTheme.topics = updatedTopics;
      newThemes[themeIndex] = updatedTheme;
      return newThemes;
    });
  };

  const getAllQuestionFiles = (): QuestionFileMap => {
    const files: QuestionFileMap = {};

    qbThemes.forEach((theme) => {
      theme.topics.forEach((topic) => {
        topic.questions.forEach((q) => {
          if (q.image?.file) {
            files[q.uuid] = q.image.file;
          }
        });
      });
    });

    return files;
  };

  const updateThemesWithFileUrls = (
    fileUrls: Record<string, string>
  ): QBTheme[] => {
    return qbThemes.map((theme) => ({
      ...theme,
      topics: theme.topics.map((topic) => ({
        ...topic,
        questions: topic.questions.map((q) =>
          fileUrls[q.uuid]
            ? {
                ...q,
                image: {
                  ...q.image,
                  value: fileUrls[q.uuid],
                },
              }
            : q
        ),
      })),
    }));
  };

  const buildStructuredQBThemes = (qbThemes: QBTheme[]): string => {
    const result: Record<string, any> = { themes: {} };

    qbThemes.forEach((theme) => {
      const themeName = formatString(theme.name);
      result.themes[themeName] = {
        label: "Theme",
        type: "select",
        topics: {},
      };

      theme.topics.forEach((topic) => {
        const topicName = formatString(topic.name);
        result.themes[themeName].topics[topicName] = {
          label: "Topic",
          type: "select",
          questions: {},
        };

        topic.questions.forEach((q) => {
          const questionPayload: any = {
            item_demand: {
              label: "Item Demand",
              value:
                typeof q.item_demand === "string"
                  ? formatString(q.item_demand)
                  : q.item_demand?.value || "",
              type: "select",
              options: qbConfig?.itemDemandOptions || [],
            },
            answer: {
              label: "Answer",
              value: formatString(q.answer || ""),
              type: "text",
            },
            image: {
              label: "Item",
              value: q.image?.value || "",
              type: "image",
              question_details: `${formatString(themeName)}|${formatString(
                topicName
              )}`,
            },
            objectives: q.objectives,
          };

          if (qbConfig?.hasExplanation) {
            questionPayload.explanation = {
              label: qbConfig?.explanationLabel || "Explanation",
              value: formatString(q.explanation || ""),
              type: "textarea",
            };
          }

          result.themes[themeName].topics[topicName].questions[q.uuid] =
            questionPayload;
        });
      });
    });

    console.log("buildStructuredQBThemes result:", result);
    return JSON.stringify(result);
  };

  const createConfigFromBackendProvidedStructure = (input: QBConfigInput) => {
    // With the new structure, questions are directly under topics with objectives as arrays
    // The input is already in the correct format, so we just pass it to setQBConfigFromStructure
    try {
      setQBConfigFromStructure(JSON.stringify(input));
    } catch (error) {
      console.error("Error in createConfigFromBackendProvidedStructure:", error);
    }
  };

  const getAllNewImagesForEditCase = (): QuestionFileMap => {
    const images: QuestionFileMap = {};

    qbThemes.forEach((theme) => {
      theme.topics.forEach((topic) => {
        topic.questions.forEach((q) => {
          // images that were prefilled but now got replaced
          if (q.image?.file && q.image?.name && q.image?.value) {
            images[q.uuid] = q.image.file;
          }
          // new images added while editing
          if (q.image?.file && q.image?.name && !q.image?.value) {
            images[q.uuid] = q.image.file;
          }
        });
      });
    });
    return images;
  };

  const areThemesEqual = () => {
    return isEqual(qbThemes, initialQbThemes);
  };

  return {
    qbThemes,
    transformedQBData,
    qbConfig,
    addQBTheme,
    removeQBTheme,
    addQBTopic,
    removeQBTopic,
    addQBQuestion,
    removeQBQuestion,
    updateQBQuestion,
    validateQBForm,
    resetQBForm,
    getQBFormData,
    setQBConfigFromStructure,
    createTransformedQB,
    updateSelectedTheme,
    updateSelectedTopic,
    getAllQuestionFiles,
    updateThemesWithFileUrls,
    buildStructuredQBThemes,
    createConfigFromBackendProvidedStructure,
    prefillQBForm,
    getAllNewImagesForEditCase,
    areThemesEqual,
  };
};
